import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CountryService } from "../../../services/user/country.services";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";

const CountryComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [countryData, setCountryData] = useState([]);
    const [filterText, setFilterText] = useState("");
    const pageNumber = location.state?.pageNumber || 1;
    const [currentPage, setCurrentPage] = useState(pageNumber);
    const rowsPerPage = 5;
    const [showModal, setShowModal] = useState(false);
    const [countryToDelete, setCountryToDelete] = useState(null);

    useEffect(() => {
        const fetchCountryData = async () => {
            try {
                const response = await CountryService.getAllCountry(selectedStore);
                setCountryData(response);
            } catch (error) {
                setCountryData([]);
            }
        };
        fetchCountryData();
    }, []);

    const columns = [
        {
            name: "Name",
            selector: row => row.countryName,
            sortable: true, cell: row => row.countryName,
            wrap: true,
        },
        {
            name: "Short Name",
            selector: row => row.shortName,
            sortable: true,
        },
        {
            name: "Active?",
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <button className={`btn btn-light radius-30 px-4 ${row.isActive ? 'text-success' : 'text-danger'}`}>
                    {row.isActive === true ? "Active" : "Inactive"}
                </button>
            ),
        },
        {
            name: "Action",
            button: true,
            width: "15%",
            cell: row => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>Edit</button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>Delete</button>
                </div>
            ),
        },
    ];

    const filteredData = countryData.filter(item =>
        Object.keys(item).some(key =>
            item[key]?.toString().toLowerCase().includes(filterText.toLowerCase())
        )
    );

    const handleSearchChange = (event) => {
        setFilterText(event.target.value)
    };

    // Slice the filtered data for the current page
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    // Calculate total pages
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // Handle the page change
    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleEditClick = (row) => {
        navigate('/country/edit', { state: { countryId: row?.countryId } });
    };

    const handleDeleteClick = (item) => {
        setCountryToDelete(item);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            if (countryToDelete) {
                await CountryService.deleteCountry(countryToDelete.countryId);
                setCountryData(prevData => prevData.filter(country => country.countryId !== countryToDelete.countryId));
                handleNotify("Country deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
            }
            setShowModal(false);
        } catch (error) {
            handleNotify(error || 'Error while deleting Country.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleAddNewClick = () => {
        navigate('/country/create', { state: { pageNumber: currentPage } });
    };

    return (
        <>
            <div className="page-wrapper m-0 p-4">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center mb-3">
                        <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                            Countries
                        </div>
                        <div className="ms-auto">
                            <button className="btn btn-primary" onClick={handleAddNewClick}>New +</button>
                        </div>
                    </div>
                    <div className="card p-4 radius-10 border-top border-0 border-4 border-primary p-4">
                        <div className="card-body">
                            <div className="d-flex justify-content-center flex-column flex-md-row flex-xs-row mb-3">
                                <div className="btn-group me-2 mb-3 mb-md-0 mb-sm-0">
                                    <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                    <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                                </div>

                                <div className="d-flex ms-auto align-items-center justify-content-center flex-md-row">
                                    <div className="m-2">
                                        <span className="fw-bold">Search:</span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search 🔍..."
                                        value={filterText}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                            </div>

                            {currentData && (
                                <div className="table-responsive">
                                    <DataTable
                                        columns={columns}
                                        data={currentData}
                                        fixedHeader
                                        responsive
                                        highlightOnHover
                                        sortIcon={<i class="fadeIn animated bx bx-sort" />}
                                    />
                                </div>
                            )}

                            <div className="d-flex mt-4 justify-content-end">
                                <div className="btn-group">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    >
                                        Prev
                                    </button>
                                    {Array.from({ length: totalPages }, (_, index) => (
                                        <button
                                            key={index}
                                            className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showModal &&
                <DeleteModelPopupComponent
                    show={showModal}
                    onClose={handleModalClose}
                    onConfirm={handleDeleteConfirm}
                    title="Confirm"
                    confirmText="Delete"
                    Message="Are you sure want to delete selected record(s)?"
                />
            }
        </>

    );
};

export default CountryComponent;
